<script setup>
import { computed, watch, ref, nextTick, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import ConnectInboxDialog from 'dashboard/components-next/pilot/pageComponents/inbox/ConnectInboxDialog.vue';
import InboxCard from 'dashboard/components-next/pilot/assistant/InboxCard.vue';
import InboxPageEmptyState from 'dashboard/components-next/pilot/pageComponents/emptyStates/InboxPageEmptyState.vue';

const store = useStore();
const dialogType = ref('');
const route = useRoute();

const assistantId = computed(() => route.params.assistantId);
const assistantUiFlags = useMapGetter('pilotAssistants/getUIFlags');
const uiFlags = useMapGetter('pilotInboxes/getUIFlags');
const isFetchingAssistant = computed(() => assistantUiFlags.value.fetchingItem);
const isFetching = computed(() => uiFlags.value.fetchingList);

const pilotInboxes = useMapGetter('pilotInboxes/getRecords');

// 确保 pilotInboxes 是数组，避免 undefined 导致的问题
const pilotInboxesArray = computed(() => pilotInboxes.value || []);
const isEmpty = computed(() => !pilotInboxesArray.value.length);

const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const handleDelete = () => {
  disconnectInboxDialog.value.dialogRef.open();
};

const connectInboxDialog = ref(null);

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};
const handleAction = ({ action, id }) => {
  selectedInbox.value = pilotInboxesArray.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

watch(
  assistantId,
  newId => {
    store.dispatch('pilotInboxes/get', {
      assistantId: newId,
    });
  },
  { immediate: true }
);

onMounted(() => {
  // 加载 assistants 列表，使助手切换器能够正常显示
  store.dispatch('pilotAssistants/get');
});
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.INBOXES.HEADER')"
    :button-label="$t('PILOT.INBOXES.ADD_NEW')"
    :button-policy="['administrator']"
    :is-fetching="isFetchingAssistant || isFetching"
    :is-empty="isEmpty"
    :show-pagination-footer="false"
    :show-know-more="false"
    @click="handleCreate"
  >
    <template #emptyState>
      <InboxPageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <InboxCard
          v-for="pilotInbox in pilotInboxesArray"
          :id="pilotInbox.id"
          :key="pilotInbox.id"
          :inbox="pilotInbox"
          @action="handleAction"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
      translation-key="INBOXES"
    />

    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
